/**
 * Find & Replace widget — floating panel pinned top-right of the canvas.
 *
 * Pure DOM surface. Search/replace logic lives in find-replace-actions.js;
 * this module just renders the inputs, results list, and emits events.
 *
 * Events emitted on the global eventBus:
 *   find-replace:close          {}
 *   find-replace:query          { query, opts: { caseSensitive, wholeWord, regex, includeFormText } }
 *   find-replace:navigate       { hit, result, globalHitIndex }
 *   find-replace:replace-one    { hit, result, replacement }
 *   find-replace:replace-all    { results, replacement, fingerprint }
 *
 * Public API (called by find-replace-actions.js):
 *   open(opts)                  Mount + focus the find input.
 *   close()                     Unmount + restore focus.
 *   setResults({ results, error, fingerprint })
 *   setCurrentIndex(idx)        Highlight current match (idx is global across all hits).
 *   setBindSuggestion(s|null)   Show or hide the "Also bind to <field>" checkbox.
 *   getOpts()                   Current toggle state.
 *   getQuery()                  Current find input value.
 *   getReplacement()            Current replace input value.
 */

import { eventBus } from '../core/events.js';
import { escapeHTML } from '../shared/html-escape.js';
import { createButton, createConfirmDialog, createPill } from './components.js';
import { icon } from './icons.js';
import { MENU_EXIT_MS } from './popover-menu.js';

let stylesInjected = false;

function injectStylesOnce() {
  if (stylesInjected) return;
  stylesInjected = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'styles/find-replace.css';
  link.setAttribute('data-pb-style', 'find-replace');
  document.head.appendChild(link);
}

const QUERY_DEBOUNCE_MS = 80;

export class FindReplaceWidget {
  constructor() {
    injectStylesOnce();
    this._el = this._build();
    this._results = [];
    this._fingerprint = null;
    this._currentIndex = 0; // global hit index across all groups
    this._collapsedGroups = new Set();
    this._bindSuggestion = null;
    this._debounceTimer = null;
    this._lastFocusBeforeOpen = null;
    this._notice = '';
    this._sharedReplaceAllConfirmed = false;
    this._isOpen = false;
    this._wire();
  }

  open({ initialQuery = '', expandReplace = false } = {}) {
    if (this._isOpen) {
      const shouldRunQuery = !!initialQuery && initialQuery !== this._input.value;
      if (initialQuery) this._setQuery(initialQuery);
      if (expandReplace) this._setReplaceVisible(true);
      this._input.focus();
      this._input.select();
      if (shouldRunQuery) this._emitQuery();
      return;
    }
    this._lastFocusBeforeOpen = document.activeElement;
    document.body.appendChild(this._el);
    // Force a reflow so the transition runs from the initial detached state.
    void this._el.offsetWidth;
    this._el.classList.add('pb-fr--open');
    if (expandReplace) this._setReplaceVisible(true);
    if (initialQuery) this._setQuery(initialQuery);
    this._input.focus();
    this._input.select();
    this._isOpen = true;
    if (initialQuery) this._emitQuery();
  }

  close() {
    if (!this._isOpen) return;
    this._el.classList.remove('pb-fr--open');
    // The exit is the menu's (consolidation C-6): CSS --pb-menu-anim-exit, removed on
    // MENU_EXIT_MS, so the fade and the removal cannot drift (contracts §9).
    setTimeout(() => {
      if (this._el.parentNode) this._el.parentNode.removeChild(this._el);
    }, MENU_EXIT_MS);
    if (this._lastFocusBeforeOpen && typeof this._lastFocusBeforeOpen.focus === 'function') {
      try {
        this._lastFocusBeforeOpen.focus();
      } catch {}
    }
    this._isOpen = false;
    eventBus.emit('find-replace:close', {});
  }

  setResults({ results, error, fingerprint }) {
    this._results = Array.isArray(results) ? results : [];
    this._fingerprint = fingerprint || null;
    this._notice = '';
    this._el.classList.toggle('pb-fr--error', !!error);
    this._el.classList.remove('pb-fr--notice');
    this._errorEl.textContent = error || '';
    this._input.classList.toggle('pb-fr-input--error', !!error);
    this._renderResults();
    this._updateCounter();
    this._updateActionStates();
  }

  setCurrentIndex(idx) {
    const total = this._totalHits();
    if (total === 0) {
      this._currentIndex = 0;
      this._updateCounter();
      return;
    }
    this._currentIndex = ((idx % total) + total) % total;
    this._updateCounter();
    // Update visual current-match highlight in the list.
    const rows = this._resultsEl.querySelectorAll('.pb-fr-hit');
    rows.forEach((r) => r.classList.remove('pb-fr-hit--current'));
    const cur = rows[this._currentIndex];
    if (cur) {
      cur.classList.add('pb-fr-hit--current');
      cur.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  setBindSuggestion(suggestion) {
    this._bindSuggestion = suggestion || null;
    this._el.classList.toggle('pb-fr--bind-suggested', !!suggestion);
    if (suggestion) {
      this._bindLabel.textContent = `Also bind to ${suggestion.label}`;
      this._bindCheckbox.checked = false;
    }
  }

  setNotice(message) {
    this._notice = message || '';
    this._el.classList.remove('pb-fr--error');
    this._input.classList.remove('pb-fr-input--error');
    this._el.classList.toggle('pb-fr--notice', !!this._notice);
    this._errorEl.textContent = this._notice;
  }

  getOpts() {
    return {
      caseSensitive: this._toggles.case.getAttribute('aria-pressed') === 'true',
      wholeWord: this._toggles.word.getAttribute('aria-pressed') === 'true',
      regex: this._toggles.regex.getAttribute('aria-pressed') === 'true',
      includeFormText: true
    };
  }

  getQuery() {
    return this._input.value;
  }
  getReplacement() {
    return this._replaceInput.value;
  }
  getBindKey() {
    return this._bindSuggestion && this._bindCheckbox.checked ? this._bindSuggestion.key : null;
  }
  getFingerprint() {
    return this._fingerprint;
  }
  getResults() {
    return this._results;
  }
  getCurrentHit() {
    if (!this._results.length) return null;
    const total = this._totalHits();
    if (!total) return null;
    let acc = 0;
    for (const r of this._results) {
      if (this._currentIndex < acc + r.hits.length) {
        return { hit: r.hits[this._currentIndex - acc], result: r };
      }
      acc += r.hits.length;
    }
    return null;
  }

  // ---------- private ----------

  _build() {
    const el = document.createElement('div');
    el.className = 'pb-fr';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Find & Replace');
    el.innerHTML = `
      <div class="pb-fr-row pb-fr-row--find">
        <span class="pb-fr-icon">${icon('search', { size: 'sm' })}</span>
        <input class="pb-fr-input" type="search" placeholder="Find" aria-label="Find" spellcheck="false" />
        <span class="pb-fr-count" data-role="count">0 of 0</span>
      </div>
      <div class="pb-fr-row pb-fr-row--replace">
        <span class="pb-fr-icon">${icon('replace', { size: 'sm' })}</span>
        <input class="pb-fr-input" data-role="replacement" type="text" placeholder="Replace" aria-label="Replace" spellcheck="false" />
      </div>
      <div class="pb-fr-bind-row">
        <label><input type="checkbox" data-role="bind-check"/><span data-role="bind-label"></span></label>
      </div>
      <div class="pb-fr-error" data-role="error"></div>
      <div class="pb-fr-results" data-role="results" role="listbox" aria-label="Search results"></div>
    `;
    // Kit controls (consolidation C-1, C-2). The data-toggle / data-action hooks are
    // what _wire() and the find-replace interaction contract address them by.
    const toggle = (key, label, name, tooltip) =>
      createButton({
        variant: 'icon',
        size: 'sm',
        label,
        pressed: false,
        ariaLabel: name,
        tooltip,
        dataset: { toggle: key }
      });
    const action = (key, glyph, name, shortcut) =>
      createButton({
        variant: 'icon',
        size: 'sm',
        icon: icon(glyph, { size: 'xs' }),
        ariaLabel: name,
        tooltip: name,
        tooltipShortcut: shortcut,
        dataset: { action: key }
      });
    const findRow = el.querySelector('.pb-fr-row--find');
    const count = findRow.querySelector('[data-role="count"]');
    findRow.insertBefore(toggle('case', 'Aa', 'Match case', 'Match case'), count);
    findRow.insertBefore(toggle('word', '[w]', 'Match whole word', 'Match whole word'), count);
    findRow.insertBefore(toggle('regex', '.*', 'Regex', 'Regex (with $1..$9 backrefs)'), count);
    findRow.append(
      action('prev', 'chevronUp', 'Previous match', 'Shift+Enter'),
      action('next', 'chevronDown', 'Next match', 'Enter'),
      action('toggle-replace', 'replace', 'Toggle replace row'),
      action('close', 'close', 'Close', 'Esc')
    );
    el.querySelector('.pb-fr-row--replace').append(
      createButton({
        variant: 'outline',
        size: 'sm',
        label: 'Replace',
        dataset: { action: 'replace-one' }
      }),
      createButton({
        variant: 'primary',
        size: 'sm',
        label: 'Replace All',
        dataset: { action: 'replace-all' }
      })
    );
    return el;
  }

  _wire() {
    this._input = this._el.querySelector('.pb-fr-input');
    this._replaceInput = this._el.querySelector('[data-role="replacement"]');
    this._countEl = this._el.querySelector('[data-role="count"]');
    this._resultsEl = this._el.querySelector('[data-role="results"]');
    this._errorEl = this._el.querySelector('[data-role="error"]');
    this._bindCheckbox = this._el.querySelector('[data-role="bind-check"]');
    this._bindLabel = this._el.querySelector('[data-role="bind-label"]');
    this._toggles = {
      case: this._el.querySelector('[data-toggle="case"]'),
      word: this._el.querySelector('[data-toggle="word"]'),
      regex: this._el.querySelector('[data-toggle="regex"]')
    };
    this._actions = {
      prev: this._el.querySelector('[data-action="prev"]'),
      next: this._el.querySelector('[data-action="next"]'),
      toggleReplace: this._el.querySelector('[data-action="toggle-replace"]'),
      close: this._el.querySelector('[data-action="close"]'),
      replaceOne: this._el.querySelector('[data-action="replace-one"]'),
      replaceAll: this._el.querySelector('[data-action="replace-all"]')
    };

    this._input.addEventListener('input', () => this._scheduleEmit());
    this._replaceInput.addEventListener('input', () =>
      eventBus.emit('find-replace:replacement-changed', { replacement: this._replaceInput.value })
    );
    this._input.addEventListener('keydown', (e) => this._onFindKeydown(e));
    this._replaceInput.addEventListener('keydown', (e) => this._onReplaceKeydown(e));
    this._el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    });

    for (const t of Object.values(this._toggles)) {
      t.addEventListener('click', () => {
        const pressed = t.getAttribute('aria-pressed') === 'true';
        t.setAttribute('aria-pressed', String(!pressed));
        this._emitQuery();
      });
    }
    this._actions.prev.addEventListener('click', () =>
      this.setCurrentIndex(this._currentIndex - 1)
    );
    this._actions.next.addEventListener('click', () =>
      this.setCurrentIndex(this._currentIndex + 1)
    );
    this._actions.toggleReplace.addEventListener('click', () =>
      this._setReplaceVisible(!this._el.classList.contains('pb-fr--replace-open'))
    );
    this._actions.close.addEventListener('click', () => this.close());
    this._actions.replaceOne.addEventListener('click', () => this._emitReplaceOne());
    this._actions.replaceAll.addEventListener('click', () => void this._emitReplaceAll());

    this._resultsEl.addEventListener('click', (e) => {
      const hit = e.target.closest('.pb-fr-hit');
      if (hit) {
        const idx = Number(hit.dataset.globalIndex);
        if (Number.isInteger(idx)) {
          this.setCurrentIndex(idx);
          this._emitNavigate();
        }
        return;
      }
      const group = e.target.closest('.pb-fr-group-header');
      if (group) {
        const groupEl = group.parentElement;
        const path = groupEl.dataset.sectionPath;
        if (this._collapsedGroups.has(path)) this._collapsedGroups.delete(path);
        else this._collapsedGroups.add(path);
        this._renderResults();
      }
    });
  }

  _scheduleEmit() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._emitQuery(), QUERY_DEBOUNCE_MS);
  }

  _emitQuery() {
    this.setNotice('');
    eventBus.emit('find-replace:query', { query: this._input.value, opts: this.getOpts() });
  }

  _emitNavigate() {
    const cur = this.getCurrentHit();
    if (!cur) return;
    eventBus.emit('find-replace:navigate', {
      hit: cur.hit,
      result: cur.result,
      globalHitIndex: this._currentIndex
    });
  }

  _emitReplaceOne() {
    const cur = this.getCurrentHit();
    if (!cur || cur.hit.isInsideBinding) return;
    eventBus.emit('find-replace:replace-one', {
      hit: cur.hit,
      result: cur.result,
      replacement: this._replaceInput.value,
      opts: this.getOpts(),
      bindKey: this.getBindKey(),
      fingerprint: this._fingerprint
    });
  }

  async _emitReplaceAll() {
    if (!this._results.length) return;
    // Single-flight: the confirms are now async, so a second Enter or click
    // while one is open would stack a second dialog over the same replacement.
    if (this._replaceAllPending) return;
    this._replaceAllPending = true;
    try {
      await this._runReplaceAll();
    } finally {
      this._replaceAllPending = false;
    }
  }

  async _runReplaceAll() {
    const replacement = this._replaceInput.value;
    if (
      replacement === '' &&
      !(await this._confirm({
        title: 'Replace with empty text?',
        message: 'Replace all matches with empty text? This will delete the matched text.',
        confirmLabel: 'Delete matches',
        danger: true
      }))
    ) {
      return;
    }
    const hasSharedHits = this._results.some(
      (r) => r.sharedKind && Array.isArray(r.hits) && r.hits.length > 0
    );
    if (hasSharedHits && !this._sharedReplaceAllConfirmed) {
      if (
        !(await this._confirm({
          title: 'Replace across every page?',
          message:
            'This includes shared navigation or footer text and will affect every page. Continue?',
          confirmLabel: 'Replace everywhere'
        }))
      ) {
        return;
      }
      this._sharedReplaceAllConfirmed = true;
    }
    eventBus.emit('find-replace:replace-all', {
      results: this._results,
      replacement,
      opts: this.getOpts(),
      bindKey: this.getBindKey(),
      fingerprint: this._fingerprint
    });
  }

  /**
   * App-native confirm. Was `window.confirm`, which blocks the whole tab, cannot
   * be styled, and sits outside the focus-trap and stacking discipline every
   * other overlay in the builder follows.
   * @returns {Promise<boolean>}
   */
  _confirm(opts) {
    return createConfirmDialog({ cancelLabel: 'Cancel', ...opts });
  }

  _onFindKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) this.setCurrentIndex(this._currentIndex - 1);
      else this.setCurrentIndex(this._currentIndex + 1);
      this._emitNavigate();
    }
  }

  _onReplaceKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) void this._emitReplaceAll();
      else this._emitReplaceOne();
    }
  }

  _setQuery(value) {
    this._input.value = value;
  }

  _setReplaceVisible(visible) {
    this._el.classList.toggle('pb-fr--replace-open', !!visible);
    if (visible) this._replaceInput.focus();
  }

  _totalHits() {
    return this._results.reduce((acc, r) => acc + r.hits.length, 0);
  }

  _updateCounter() {
    const total = this._totalHits();
    if (total === 0) this._countEl.textContent = '0 of 0';
    else this._countEl.textContent = this._currentIndex + 1 + ' of ' + total;
  }

  _updateActionStates() {
    const total = this._totalHits();
    const cur = this.getCurrentHit();
    this._actions.prev.disabled = total === 0;
    this._actions.next.disabled = total === 0;
    const currentIsBound = !!(cur && cur.hit.isInsideBinding);
    this._actions.replaceOne.disabled = total === 0 || currentIsBound;
    this._actions.replaceOne.setAttribute(
      'data-tooltip',
      currentIsBound
        ? 'This text is bound to Client Info — edit the Client Info panel to change it.'
        : 'Replace current match (Enter)'
    );
    this._actions.replaceAll.disabled = total === 0;
  }

  _renderResults() {
    const total = this._totalHits();
    if (!this._input.value) {
      this._resultsEl.innerHTML = '';
      return;
    }
    if (total === 0) {
      this._resultsEl.innerHTML = '<div class="pb-fr-empty">No matches</div>';
      return;
    }
    let globalIdx = 0;
    const groups = this._results.map((r) => {
      const collapsed = this._collapsedGroups.has(r.sectionPath);
      const sharedLabel = r.sharedKind ? ' · shared' : '';
      const pageLabel =
        r.pageName ||
        (r.sharedKind === 'navigation'
          ? 'Site nav'
          : r.sharedKind === 'footer'
            ? 'Site footer'
            : '');
      const headerLabel = escapeHTML(pageLabel + sharedLabel);
      const body = r.hits
        .map((hit) => {
          const myIdx = globalIdx++;
          const ctx =
            '<span class="pb-fr-hit-ctx">' +
            escapeHTML(hit.beforeCtx) +
            '</span>' +
            '<span class="pb-fr-hit-match">' +
            escapeHTML(hit.match) +
            '</span>' +
            '<span class="pb-fr-hit-ctx">' +
            escapeHTML(hit.afterCtx) +
            '</span>';
          // Status badges are kit pill tones (consolidation C-3).
          const badge = (label, tone, tooltip) => {
            const pill = createPill({ label, tone });
            pill.setAttribute('data-tooltip', tooltip);
            return pill.outerHTML;
          };
          const badges = [];
          if (r.sharedKind) {
            badges.push(badge('⚠ shared', 'danger', 'Shared section — affects every page'));
          }
          if (hit.isInsideBinding) {
            badges.push(
              badge(
                '🔒 bound',
                'accent',
                'Inside a Client Info binding — edit Client Info to change'
              )
            );
          }
          if (hit.isInsideForm) badges.push(badge('📝 form', 'warning', 'Inside a form control'));
          return (
            '<button type="button" class="pb-fr-hit" role="option" data-global-index="' +
            myIdx +
            '">' +
            ctx +
            (badges.length ? '<span class="pb-fr-hit-badges">' + badges.join('') + '</span>' : '') +
            '</button>'
          );
        })
        .join('');
      return (
        '<div class="pb-fr-group" data-section-path="' +
        escapeHTML(r.sectionPath) +
        '" aria-expanded="' +
        (collapsed ? 'false' : 'true') +
        '">' +
        '<div class="pb-fr-group-header">' +
        '<span class="pb-fr-group-chev">' +
        icon('chevronRight', { size: 'xs' }) +
        '</span>' +
        '<span class="pb-fr-group-label">' +
        headerLabel +
        '</span>' +
        '<span class="pb-fr-group-count">' +
        r.hits.length +
        '</span>' +
        '</div>' +
        '<div class="pb-fr-group-body">' +
        body +
        '</div>' +
        '</div>'
      );
    });
    this._resultsEl.innerHTML = groups.join('');
    // Restore current-match highlight after re-render.
    const rows = this._resultsEl.querySelectorAll('.pb-fr-hit');
    const cur = rows[this._currentIndex];
    if (cur) cur.classList.add('pb-fr-hit--current');
  }
}
